import React from 'react';
import PublicLayout from '../components/layouts/PublicLayout';

const contentSections = [
  {
    heading: 'Privacy Policy',
    paragraphs: [
      'Last Updated: November 8, 2025',
      <>
        At Popout, we value and respect your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our public website or web application at{' '}
        <a
          href="https://popout.ae"
          className="text-[#5E5C61] underline transition-colors duration-200 hover:text-[#a463ff]"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://popout.ae
        </a>
        . By accessing or using the Site, you consent to the practices described in this policy.
      </>,
    ],
  },
  {
    heading: '1. Information We Collect',
    description:
      'We collect several types of information for various purposes to provide and improve our service to you.',
    items: [
      {
        title: 'Personal Data',
        text: 'While using our Site, we may ask you to provide certain personally identifiable information that can be used to contact or identify you ("Personal Data"). This may include, but is not limited to:',
        bullets: ['Full name', 'Email address', 'Phone number', 'Mailing or billing address', 'Payment information (when you complete a transaction)'],
      },
      {
        title: 'Usage Data',
        text: 'We may also collect information about how the Site is accessed and used ("Usage Data"). This Usage Data may include your device’s IP address, browser type, browser version, the pages you visit, the time and date of your visit, unique device identifiers, and other diagnostic data.',
      },
      {
        title: 'Cookies Data',
        text: 'We use cookies and similar tracking technologies to track the activity on our Site and store certain information. Cookies are small data files placed on your device. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent, but doing so may limit certain Site features.',
      },
    ],
  },
  {
    heading: '2. How We Use Your Information',
    description: 'We use the collected data for multiple purposes that help us operate, secure, and enhance Popout.',
    bullets: [
      'To provide and maintain our Site',
      'To notify you about changes to our Site or services',
      'To allow you to participate in interactive features of our Site',
      'To provide customer support',
      'To gather analysis or valuable information so that we can improve our Site',
      'To monitor the usage of our Site',
      'To detect, prevent, and address technical issues',
      'To fulfill any other purpose for which you provide it',
    ],
  },
  {
    heading: '3. Sharing Your Information',
    description: 'We do not sell or rent your Personal Data to third parties. However, we may share information in the following situations:',
    bullets: [
      'With service providers to monitor and analyze the use of our Site',
      'With business partners, affiliates, or subsidiaries for business purposes',
      'When required by law, such as to comply with a subpoena or similar legal process',
      'To protect the rights, property, or safety of Popout, our users, or others',
    ],
  },
  {
    heading: '4. Data Security',
    description: 'We take the security of your data seriously and use commercially acceptable means to protect it. However, no method of transmission over the internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data.',
  },
];

const applyParagraphClass = (text) => (
  <p className="paragraph text-base sm:text-[20.75px]">
    {text}
  </p>
);

const PrivacyPolicy = () => {
  return (
    <PublicLayout showSupportCTA={false}>
      <div className="bg-bodyMain">
        <section className="bg-bodyMain py-6 sm:py-20">
          <div className="w-full mx-auto px-4 sm:px-12 text-center">
            <h1 className="font-extrabold text-[32px] sm:text-[64px] lg:text-[86px] leading-[1.1] sm:leading-[1.05] tracking-tight">
              <span style={{ color: '#a463ff' }}>OUR </span>
              <span style={{ color: '#000000' }}>PRIVACY </span>
              <span style={{ color: '#ffb600' }}>POLICY</span>
            </h1>
          </div>
        </section>

        <section className="relative z-10 bg-bodyMain">
          <div className="w-full mx-auto px-4 sm:px-12 py-4 sm:py-16">
            <div className="space-y-6 sm:space-y-12 text-left text-[#5E5C61]">
              {contentSections.map((section) => (
                <div key={section.heading} className="pb-4 sm:pb-10 last:pb-0">
                  <h2 className="text-xl sm:text-2xl font-bold text-[#5E5C61] mb-2 sm:mb-0">{section.heading}</h2>
                  {section.paragraphs &&
                    section.paragraphs.map((paragraph) => (
                      <div key={paragraph} className="mt-3 sm:mt-4">
                        {applyParagraphClass(paragraph)}
                      </div>
                    ))}
                  {section.description && (
                    <div className="mt-3 sm:mt-4">
                      {applyParagraphClass(section.description)}
                    </div>
                  )}

                  {section.items && (
                    <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-6">
                      {section.items.map((item) => (
                        <div key={item.title} className="mt-3 sm:mt-0">
                          <h3 className="text-base sm:text-lg font-semibold text-[#5E5C61]">{item.title}</h3>
                          <div className="mt-1.5 sm:mt-3">
                            {applyParagraphClass(item.text)}
                          </div>
                          {item.bullets && (
                            <ul className="mt-1.5 sm:mt-3 list-disc list-inside space-y-1.5 sm:space-y-2 text-base sm:text-[20.75px] text-[#5E5C61] leading-[1.6]">
                              {item.bullets.map((bullet) => (
                                <li key={bullet} className="!mb-0">
                                  {bullet}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {section.bullets && (
                    <ul className="mt-3 sm:mt-6 space-y-1.5 sm:space-y-3 list-disc list-inside text-base sm:text-[20.75px] text-[#5E5C61] leading-[1.6]">
                      {section.bullets.map((point) => (
                        <li key={point} className="!mb-0">
                          {point}
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.contact && (
                    <div className="mt-4 sm:mt-6 space-y-2 sm:space-y-3">
                      <p className="paragraph !mb-0">
                        Email: <span className="text-[#4C1D95] font-medium">{section.contact.email}</span>
                      </p>
                      <p className="paragraph !mb-0">Phone: {section.contact.phone}</p>
                      <p className="paragraph !mb-0">Address: {section.contact.address}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </PublicLayout>
  );
};

export default PrivacyPolicy;
